import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';

const INVITE_TOKEN_KEY = 'gb_invite_token';

export function setInviteToken(token: string) {
  localStorage.setItem(INVITE_TOKEN_KEY, token);
}

export function getInviteToken(): string | null {
  return localStorage.getItem(INVITE_TOKEN_KEY);
}

export function clearInviteToken() {
  localStorage.removeItem(INVITE_TOKEN_KEY);
}

/**
 * Hook that checks for an invite token after signup.
 * Sources (in priority order): URL query param, localStorage.
 * If found, claims the token to assign the user to the invited brand.
 * Should be placed in an authenticated component (e.g., ProtectedRoute).
 *
 * Returns { claiming: boolean } so the parent can show a loading state.
 */
export function useInviteClaim() {
  const [searchParams] = useSearchParams();
  const utils = trpc.useUtils();
  const claimMutation = trpc.invites.claim.useMutation({
    // Awaited: the mutation stays pending (so ProtectedRoute keeps "Setting up
    // your account") until users.me has refetched with the new brand and its
    // onboarding stage. Otherwise a fresh invitee briefly gets the portal frame,
    // brandless, and portal queries fire before the jump to /onboarding.
    onSuccess: async () => {
      clearInviteToken();
      await utils.users.me.invalidate();
    },
    onError: () => {
      clearInviteToken();
    },
  });

  const claimed = useRef(false);

  useEffect(() => {
    // Check URL first (survives tab switches), then localStorage
    const tokenFromUrl = searchParams.get('invite');
    const token = tokenFromUrl || getInviteToken();

    if (token && !claimed.current && !claimMutation.isPending) {
      claimed.current = true;
      claimMutation.mutate({ token });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Before the effect runs, a token in the URL or storage already means "claiming".
  const pending = !!(searchParams.get('invite') || getInviteToken()) && !claimed.current;
  return { claiming: claimMutation.isPending || pending };
}
